import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { GridCell, GridRow, LifeProfile, Milestone, ViewMode } from '../types/life';
import type { ThemeState } from '../hooks/useTheme';
import { useNow } from '../hooks/useNow';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { YEARS_AHEAD, buildLifeWeekRows, buildMonthDayRows, buildYearStrip, computeStats, horizonOf, lifeWeekKeyOf, milestoneCellKeys, milestoneFocusKey, parseDate, toISODate } from '../utils/lifeGrid';
import { remainingCounters } from '../utils/counters';
import { TopBar } from './TopBar';
import { TodayStrip } from './TodayStrip';
import { MilestonePanel } from './MilestonePanel';
import { MilestoneDialog, type MilestoneDraft } from './MilestoneDialog';
import { Modal } from './Modal';
import { ProfileForm } from './ProfileForm';
import { NowView } from './views/NowView';
import { LifetimeView, type LifetimeStyle } from './views/LifetimeView';
interface LifeCalendarProps {
  profile: LifeProfile;
  milestones: Milestone[];
  initialView: ViewMode;
  lifetimeStyle: LifetimeStyle;
  theme: ThemeState;
  onSaveProfile: (profile: LifeProfile) => void;
  onUpsertMilestone: (milestone: Milestone) => void;
  onRemoveMilestone: (id: string) => void;
}
function pad(n: number): string {
  return String(n).padStart(2, '0');
}
export function LifeCalendar({
  profile,
  milestones,
  initialView,
  lifetimeStyle,
  theme,
  onSaveProfile,
  onUpsertMilestone,
  onRemoveMilestone
}: LifeCalendarProps) {
  const gridNow = useNow(60_000);
  const reduce = useReducedMotion();
  const [view, setView] = useState<ViewMode>(initialView);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<MilestoneDraft | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const scrollRef = useRef<HTMLElement | null>(null);
  const compact = useMediaQuery('(max-width: 640px)');
  const stripColumns = compact ? 7 : 14;
  const year = gridNow.getFullYear();
  const stats = useMemo(() => computeStats(profile, gridNow), [profile, gridNow]);
  const stripRows = useMemo(() => buildYearStrip(profile, gridNow, stripColumns), [profile, gridNow, stripColumns]);
  const dayRows = useMemo(() => buildMonthDayRows(profile, gridNow, year), [profile, gridNow, year]);
  const lifeRows = useMemo(() => buildLifeWeekRows(profile, gridNow), [profile, gridNow]);
  const counters = useMemo(() => remainingCounters(profile, gridNow), [profile, gridNow]);
  const stripLastYear = Math.min(year + YEARS_AHEAD, horizonOf(profile).getFullYear());
  const milestoneByCell = useMemo(() => {
    const map = new Map<string, Milestone>();
    milestones.forEach(milestone => {
      milestoneCellKeys(profile, milestone).forEach(key => {
        if (!map.has(key)) map.set(key, milestone);
      });
    });
    return map;
  }, [milestones, profile]);

  // Держим «сегодня» (или выбранный майлстоун) в поле зрения на вьюхе «Сейчас».
  useEffect(() => {
    if (view !== 'now') return;
    const today = new Date();
    const target = focusedKey ?? `d-${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const frame = window.requestAnimationFrame(() => {
      const el = scrollRef.current?.querySelector(`[data-cell="${target}"]`);
      el?.scrollIntoView({
        block: 'center',
        behavior: focusedKey ? 'smooth' : 'auto'
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [view, focusedKey]);
  useEffect(() => {
    if (!focusedKey) return;
    const id = window.setTimeout(() => setFocusedKey(null), 3000);
    return () => window.clearTimeout(id);
  }, [focusedKey]);
  const openDraft = (key: string, next: MilestoneDraft) => {
    const existing = milestoneByCell.get(key);
    setDraft(existing ? {
      id: existing.id,
      title: existing.title,
      granularity: existing.granularity,
      date: existing.date
    } : next);
  };
  const handleSelectYear = (cell: GridCell) => openDraft(cell.key, {
    title: '',
    granularity: 'years',
    date: toISODate(cell.start)
  });
  const handleSelectMonth = (row: GridRow) => {
    const [, y, m] = row.key.split('-');
    openDraft(row.key, {
      title: '',
      granularity: 'months',
      date: `${y}-${m}-01`
    });
  };
  const handleSelectDay = (cell: GridCell) => openDraft(cell.key, {
    title: '',
    granularity: 'days',
    date: toISODate(cell.start)
  });
  const handleSelectLifeWeek = (cell: GridCell) => openDraft(cell.key, {
    title: '',
    granularity: 'weeks',
    date: toISODate(cell.start)
  });
  const handleFocusMilestone = (milestone: Milestone) => {
    const date = parseDate(milestone.date);
    const visibleInNow = milestone.granularity === 'years' ? date.getFullYear() <= stripLastYear : milestone.granularity !== 'weeks' && date.getFullYear() === year;
    if (visibleInNow) {
      setView('now');
      setFocusedKey(milestoneFocusKey(profile, milestone));
    } else {
      setView('life');
      setFocusedKey(lifeWeekKeyOf(profile, date));
    }
  };
  return <div className="bg-paper text-ink flex h-full w-full flex-col" data-id="element-3" data-mp="16l8wyorro4hv">
      <TopBar view={view} onViewChange={setView} stats={stats} profile={profile} theme={theme} onOpenSettings={() => setSettingsOpen(true)} data-id="element-4" data-mp="16kfysc6vinbc" />

      <main ref={scrollRef} className={view === 'life' ? 'min-h-0 flex-1 overflow-hidden px-4 py-5 sm:px-6 sm:py-6' : 'thin-scroll min-h-0 flex-1 overflow-auto px-4 py-6 sm:px-6 sm:py-8'} data-id="element-5" data-mp="xipzkk6r2k30">
        <AnimatePresence mode="wait" initial={false} data-id="element-6" data-mp="9vrlft63ro89">
          <motion.div key={view} className={view === 'life' ? 'h-full' : ''} style={view === 'now' && !compact ? {
          minWidth: 900
        } : undefined} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: view === 'life' ? 1.01 : 0.99
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: view === 'life' ? 0.99 : 1.01
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-7" data-mp="9svpgadtf9mg">
            {view === 'now' ? <NowView stripRows={stripRows} stripColumns={stripColumns} dayRows={dayRows} year={year} now={gridNow} compact={compact} counters={counters} milestoneByCell={milestoneByCell} focusedKey={focusedKey} onSelectYear={handleSelectYear} onSelectMonth={handleSelectMonth} onSelectDay={handleSelectDay} data-id="element-8" data-mp="1xry2om4eb432" /> : <LifetimeView rows={lifeRows} now={gridNow} style={lifetimeStyle} compact={compact} milestoneByCell={milestoneByCell} focusedKey={focusedKey} onSelectCell={handleSelectLifeWeek} data-id="element-9" data-mp="2bjmfysqdrylm" />}
          </motion.div>
        </AnimatePresence>
      </main>

      <TodayStrip data-id="element-10" data-mp="v0n4b1dnsxof" />

      <MilestonePanel milestones={milestones} onAdd={() => setDraft({
      title: '',
      granularity: view === 'life' ? 'weeks' : 'days',
      date: toISODate(new Date())
    })} onEdit={m => setDraft({
      id: m.id,
      title: m.title,
      granularity: m.granularity,
      date: m.date
    })} onFocus={handleFocusMilestone} data-id="element-11" data-mp="uy0zvam2eyaf" />

      <MilestoneDialog open={Boolean(draft)} draft={draft} onClose={() => setDraft(null)} onSave={milestone => {
      onUpsertMilestone(milestone);
      setDraft(null);
      handleFocusMilestone(milestone);
    }} onDelete={id => {
      onRemoveMilestone(id);
      setDraft(null);
    }} data-id="element-12" data-mp="1dptqiydnw7ez" />

      <Modal open={settingsOpen} title="Horizon" onClose={() => setSettingsOpen(false)} data-id="element-13" data-mp="3pg5v4gif30lw">
        <ProfileForm initial={profile} submitLabel="Recalculate" onCancel={() => setSettingsOpen(false)} onSubmit={next => {
        onSaveProfile(next);
        setSettingsOpen(false);
      }} data-id="element-14" data-mp="3pi0e3pa4oinc" />
      </Modal>
    </div>;
}